import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { ErrorBoundary } from '@scoir/error-reporter'
import { AVAILABLE_REDIRECTS, getRedirectURLToEquivalentPage, setSignedInApp } from '@scoir/redirects'
import { Metadata } from '@scoir/tracking'
import { get } from 'lodash'
import { connect } from 'react-redux'

import { getUnauthedFallbackUrl } from 'common/api'
import { hasBearerToken } from 'common/bearer'
import initializeGoogleTag from 'common/google-tag'
import { APP, FIELDS, PUBLIC_APP } from 'common/metadata-constants'
import getSessionId from 'common/track-events-session-id'
import window from 'common/window'
import { AuthShieldContext, IAuthShieldContextValue, useIsLoggingOut } from 'components/AuthShield'
import { isCurrentRouteUnauthed, registerUnauthedRoute } from 'components/AuthShield'
import ExternalRedirect from 'components/ExternalRedirect'
import { fetchAppProfile, selectAppProfile } from 'store/app-profile'
import { fetchCurrentAppCycle, selectApplicationCycle } from 'store/application-cycle'
import { actions as careerTitlesActions } from 'store/career-titles'
import { actions as collegeGuidanceNetworkActions } from 'store/college-guidance-network'
import { actions as contentTagsActions } from 'store/content-tags'
import { actions as featuresToggleActions } from 'store/feature-toggle'
import { actions as inviterActions } from 'store/inviters'
import { actions as microSurveyActions } from 'store/microsurvey'
import { actions as myMatchesMessagingActions } from 'store/my-matches-messaging'
import { actions as parentsActions } from 'store/parents'
import { actions as prospectActions } from 'store/prospects'
import { actions as savedPublicCollegeSearchActions } from 'store/saved-public-college-search'
import { actions as userActions } from 'store/user'
import { selectPermissions } from 'store/user'

import TrackRememberedUrl from './RememberedRedirect/TrackRememberedUrl'

registerUnauthedRoute(import.meta.env.BASE_URL + '/')

interface IUserStartupProps {
  fetchAppProfile?: () => void
  fetchCurrentAppCycle?: () => void
  fetchContentTags?: () => void
  fetchCareerTitles?: () => void
  fetchFeatureToggles?: () => void
  fetchParents?: () => void
  fetchProspects?: () => void
  fetchInviters?: () => void
  clearSavedPublicSearch?: () => void
  fetchUser?: () => Promise<any>
  fetchMicroSurveyTags?: () => Promise<any>
  fetchMyMatchesSenders?: () => Promise<any>
  fetchCollegeGuidanceNetworkIntegration?: () => Promise<any>
  appProfileConfig?: any
  careerTitles?: any
  children?: any
  contentTags?: any
  currentAppCycle?: number
  features?: any
  prospectsLoaded?: boolean
  isECRStudent?: boolean
  userId?: string
}

const getSignInURL = () => `${window.location.origin}${getUnauthedFallbackUrl()}`
/**
 * This function will return the URL to redirect the user to.
 * If there is an equivalent page in another app, it will return that.
 * If there is not, it will return the unauthed fallback url (sign in).
 * @returns string
 */
const getRedirectURL = () => {
  const equivalentPageURL = getRedirectURLToEquivalentPage()
  const signInUrl = getSignInURL()
  return equivalentPageURL || signInUrl
}

const useFnOnce = (fn?: () => any, canLoad?: boolean) => {
  const [hasRun, setHasRun] = useState(false)
  const needsToRun = fn && canLoad && !hasRun
  useEffect(() => {
    if (needsToRun) {
      const returned = fn()
      if (typeof returned?.finally === 'function') {
        returned.finally(() => {
          setHasRun(true)
        })
      } else {
        setHasRun(true)
      }
    }
  }, [needsToRun, fn, canLoad])
  return hasRun
}

const useFetchUser = (
  hasToken: boolean,
  fetchUser?: () => Promise<any>,
  userId?: string,
  hasFetchedFeatures?: boolean,
) => {
  const hasFetchedUser = useRef(false)
  const needsToFetchUser = hasFetchedFeatures && fetchUser && hasToken && !userId && !hasFetchedUser.current

  useEffect(() => {
    if (needsToFetchUser) {
      fetchUser()
      hasFetchedUser.current = true
    }
  }, [needsToFetchUser, fetchUser])
  return hasFetchedUser.current
}

const useIsAuthed = (hasFetchedUser: boolean, userId?: string, hasFetchedFeatures?: boolean) => {
  const { isAuthed, setIsAuthed } = useContext(AuthShieldContext) as IAuthShieldContextValue
  useEffect(() => {
    if (hasFetchedFeatures && !isAuthed && hasFetchedUser && userId) {
      setIsAuthed(true)
    }
  }, [isAuthed, hasFetchedFeatures, hasFetchedUser, userId, setIsAuthed])
  return isAuthed
}

const useInitialize = (props: IUserStartupProps, hasToken: boolean) => {
  const {
    fetchAppProfile,
    fetchCurrentAppCycle,
    fetchContentTags,
    fetchCareerTitles,
    fetchFeatureToggles,
    fetchParents,
    fetchProspects,
    fetchUser,
    fetchInviters,
    clearSavedPublicSearch,
    fetchMicroSurveyTags,
    fetchMyMatchesSenders,
    fetchCollegeGuidanceNetworkIntegration,
    careerTitles = {},
    contentTags = {},
    userId,
    isECRStudent,
  } = props

  const { tagsRequested, isLoading } = contentTags
  const { titlesRequested, isLoadingTitles } = careerTitles
  const canLoadContentTags = !tagsRequested && !isLoading
  const canLoadCareerTitles = !titlesRequested && !isLoadingTitles

  const hasFetchedFeatures = useFnOnce(fetchFeatureToggles, true) // call on first load
  const hasFetchedUser = useFetchUser(hasToken, fetchUser, userId, hasFetchedFeatures)
  const isAuthed = useIsAuthed(hasFetchedUser, userId, hasFetchedFeatures)
  const isAuthedAndNotECR = isAuthed && !isECRStudent
  const isECR = isAuthed && isECRStudent
  const hasFetchedParents = useFnOnce(fetchParents, isAuthedAndNotECR)
  const hasFetchedProfile = useFnOnce(fetchAppProfile, isAuthedAndNotECR)
  const hasFetchedProspects = useFnOnce(fetchProspects, isAuthedAndNotECR)
  const hasFetchedCycle = useFnOnce(fetchCurrentAppCycle, isAuthedAndNotECR)
  const hasFetchedContent = useFnOnce(fetchContentTags, isAuthed && canLoadContentTags) || !canLoadContentTags
  const hasFetchedCareers = useFnOnce(fetchCareerTitles, isAuthed && canLoadCareerTitles) || !canLoadCareerTitles
  const hasFetchedMicroSurveyTags = useFnOnce(fetchMicroSurveyTags, isAuthedAndNotECR)
  const hasFetchedMyMatchesSenders = useFnOnce(fetchMyMatchesSenders, isAuthedAndNotECR)

  useFnOnce(fetchInviters, isAuthedAndNotECR)
  useFnOnce(clearSavedPublicSearch, isAuthed)
  useFnOnce(() => Promise.resolve(setSignedInApp('student', AVAILABLE_REDIRECTS.COLLEGE_PROFILE)), isAuthed)
  useFnOnce(fetchCollegeGuidanceNetworkIntegration, isAuthed)

  const hasInitialized =
    hasFetchedFeatures &&
    (hasFetchedParents || isECR) &&
    (hasFetchedProfile || isECR) &&
    (hasFetchedProspects || isECR) &&
    (hasFetchedCycle || isECR) &&
    hasFetchedContent &&
    hasFetchedCareers &&
    (hasFetchedMicroSurveyTags || isECR) &&
    (hasFetchedMyMatchesSenders || isECR)

  const willFetchUser = hasToken && !hasFetchedUser
  const attemptingToInitialize = hasFetchedUser && !hasInitialized
  const isInitializing = !hasFetchedFeatures || willFetchUser || attemptingToInitialize

  // initialize google tag for unauthed users
  useFnOnce(() => {
    return initializeGoogleTag({
      user_app: PUBLIC_APP,
      scoir_session_id: getSessionId(PUBLIC_APP),
    })
  }, !hasToken)

  return {
    isInitializing,
    hasInitialized,
    isAuthed,
    hasFetchedFeatures,
  }
}

const UserStartup = (props: IUserStartupProps) => {
  const currentRouteUnauthAllowed = isCurrentRouteUnauthed()
  const hasToken = useMemo(() => hasBearerToken(), [])
  const isLoggingOut = useIsLoggingOut()
  const { isInitializing, hasInitialized, isAuthed, hasFetchedFeatures } = useInitialize(props, hasToken)
  const app = isAuthed ? APP : PUBLIC_APP
  const unauthedButCanContinue = !isAuthed && currentRouteUnauthAllowed && hasFetchedFeatures
  const redirectUrl = getRedirectURL()
  const shouldRedirectToEquivalentPageInAnoterApp =
    unauthedButCanContinue && !!redirectUrl && redirectUrl !== window.location.href && redirectUrl !== getSignInURL()
  const unauthedPageRestricted = !isAuthed && currentRouteUnauthAllowed && !hasFetchedFeatures && hasFetchedFeatures
  const notAnUnauthedPage = !isAuthed && !currentRouteUnauthAllowed
  const isSigningIn = hasToken && !isAuthed
  const canShowTheApp =
    !isInitializing && (hasInitialized || unauthedButCanContinue) && !shouldRedirectToEquivalentPageInAnoterApp
  const shouldRedirectToSignIn =
    !isSigningIn && (notAnUnauthedPage || unauthedPageRestricted || shouldRedirectToEquivalentPageInAnoterApp)

  return isLoggingOut ? (
    <ExternalRedirect to={`${window.location.origin}/logout?userType=Student`} />
  ) : canShowTheApp ? (
    <ErrorBoundary>
      <Metadata value={{ [FIELDS.APP]: app }}>{props.children}</Metadata>
    </ErrorBoundary>
  ) : shouldRedirectToSignIn ? (
    <React.Fragment>
      <ExternalRedirect to={redirectUrl} />
      <TrackRememberedUrl />
    </React.Fragment>
  ) : null
}

export default connect(
  (store: any) => {
    const isECRStudent = !selectPermissions(store).includes('VIEW_COLLEGE_PROSPECTS_FOR_SELF')

    return {
      userId: get(store, 'user.record.Id'),
      features: store.features.record,
      prospectsLoaded: store.prospects.loaded,
      currentAppCycle: selectApplicationCycle(store),
      appProfileConfig: selectAppProfile(store),
      contentTags: store.contentTags,
      careerTitles: store.careerTitles,
      isECRStudent,
    }
  },
  {
    fetchFeatureToggles: featuresToggleActions.fetchFeatureToggles,
    fetchUser: userActions.fetchUser,
    fetchProspects: prospectActions.fetchProspects,
    fetchParents: parentsActions.fetchParents,
    fetchAppProfile,
    fetchCurrentAppCycle,
    fetchContentTags: contentTagsActions.fetchContentTags,
    fetchCareerTitles: careerTitlesActions.fetchCareerTitles,
    fetchInviters: inviterActions.fetchInviters,
    clearSavedPublicSearch: savedPublicCollegeSearchActions.clearSavedPublicCollegeSearch,
    fetchMicroSurveyTags: microSurveyActions.fetchMicroSurveyTags,
    fetchMyMatchesSenders: myMatchesMessagingActions.fetchMyMatchesSenders,
    fetchCollegeGuidanceNetworkIntegration: collegeGuidanceNetworkActions.fetchCollegeGuidanceNetworkIntegration,
  },
)(UserStartup)
